import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Col,
  Input,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Tabs,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { WarningOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ControlTowerSectionCard } from '@/components/control-tower/ControlTowerSectionCard';
import { ControlTowerMetricStrip } from '@/components/control-tower/ControlTowerMetricStrip';
import { ControlTowerMetricTile } from '@/components/control-tower/ControlTowerMetricTile';
import { EmptyQueueState } from '@/components/sales/control-tower/EmptyQueueState';
import {
  getFinanceCollectionsPayablesQueue,
  type CollectionsPayablesQueueMode,
  type CustomerOutstandingDetailRow,
  type FinanceAgingBucket,
  type FinanceDashboardActionSummary,
  type SupplierOutstandingDetailRow,
} from '@/services/financeDashboard.service';

const { Text } = Typography;

const agingColor: Record<FinanceAgingBucket, string> = {
  '0-30': 'green',
  '31-60': 'gold',
  '61-90': 'orange',
  '90+': 'red',
};

function agingBucketFromDays(days: number): FinanceAgingBucket {
  if (days <= 30) return '0-30';
  if (days <= 60) return '31-60';
  if (days <= 90) return '61-90';
  return '90+';
}

type QueueRow = CustomerOutstandingDetailRow | SupplierOutstandingDetailRow;

export const FinanceOverdueFollowUpZone: React.FC<{
  summary: FinanceDashboardActionSummary;
  asOnDate?: string;
  formatCurrency: (v: number) => string;
  initialMode?: CollectionsPayablesQueueMode;
}> = ({ summary, asOnDate, formatCurrency, initialMode = 'overdue_ar' }) => {
  const { t } = useTranslation('finance');
  const { t: tc } = useTranslation('common');
  const navigate = useNavigate();
  const [mode, setMode] = useState<CollectionsPayablesQueueMode>(initialMode);
  const [search, setSearch] = useState('');
  const [searchApplied, setSearchApplied] = useState('');
  const [aging, setAging] = useState<FinanceAgingBucket | undefined>();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  useEffect(() => {
    setMode(initialMode);
    setPage(1);
  }, [initialMode]);

  const isOverdueMode = mode.startsWith('overdue_');
  const isAr = mode.endsWith('_ar');

  const { data, isFetching, isError, error, refetch } = useQuery({
    queryKey: [
      'fin-collections-payables-queue',
      mode,
      page,
      pageSize,
      aging,
      searchApplied,
      asOnDate,
    ],
    queryFn: () =>
      getFinanceCollectionsPayablesQueue({
        mode,
        page,
        pageSize,
        aging_bucket: isOverdueMode ? aging : undefined,
        search: searchApplied || undefined,
        as_on_date: asOnDate,
      }),
    placeholderData: (prev) => prev,
  });

  const arOverdue = summary.overdue_customer_invoice_count ?? 0;
  const apOverdue = summary.overdue_supplier_invoice_count ?? 0;
  const arDueSoon = summary.due_soon_customer_invoice_count ?? 0;
  const apDueSoon = summary.due_soon_supplier_invoice_count ?? 0;
  const agingCounts = data?.aging ?? (isAr ? summary.overdue_customer_aging : summary.overdue_supplier_aging);
  const total = data?.total ?? 0;
  const totalValue = data?.total_value ?? 0;
  const rows = (data?.rows ?? []) as QueueRow[];

  const arColumns: ColumnsType<CustomerOutstandingDetailRow> = [
    {
      title: t('invoice'),
      dataIndex: 'invoice_number',
      key: 'invoice_number',
      width: 140,
      ellipsis: true,
      render: (v: string, r) => (
        <a onClick={() => navigate(`/growth/invoices/${r.invoice_id}`)}>{v}</a>
      ),
    },
    {
      title: t('customer'),
      dataIndex: 'customer_name',
      key: 'customer_name',
      width: 180,
      ellipsis: true,
    },
    {
      title: t('due'),
      dataIndex: 'due_date',
      key: 'due_date',
      width: 110,
      render: (v: string) => <span style={{ whiteSpace: 'nowrap' }}>{v || '—'}</span>,
    },
    ...(isOverdueMode
      ? ([
          {
            title: t('controlTower.aging'),
            dataIndex: 'overdue_days',
            key: 'overdue_days',
            width: 120,
            defaultSortOrder: 'descend' as const,
            sorter: (a: CustomerOutstandingDetailRow, b: CustomerOutstandingDetailRow) =>
              a.overdue_days - b.overdue_days,
            render: (d: number) => {
              const bucket = agingBucketFromDays(d);
              return (
                <Tag color={agingColor[bucket]} style={{ marginInlineEnd: 0 }}>
                  {t('controlTower.agingDaysBucket', { days: d, bucket })}
                </Tag>
              );
            },
          },
        ] as ColumnsType<CustomerOutstandingDetailRow>)
      : []),
    {
      title: t('balance'),
      dataIndex: 'balance_due',
      key: 'balance_due',
      align: 'right',
      width: 120,
      render: (v: number) => (
        <span style={{ whiteSpace: 'nowrap' }}>{formatCurrency(v ?? 0)}</span>
      ),
    },
    {
      title: '',
      key: 'action',
      width: 80,
      fixed: 'right',
      render: (_: unknown, r) => (
        <Button type="link" size="small" onClick={() => navigate(`/growth/invoices/${r.invoice_id}`)}>
          {t('open')}
        </Button>
      ),
    },
  ];

  const apColumns: ColumnsType<SupplierOutstandingDetailRow> = [
    {
      title: t('controlTower.colBill'),
      dataIndex: 'invoice_number',
      key: 'invoice_number',
      width: 140,
      ellipsis: true,
      render: (v: string, r) => (
        <a onClick={() => navigate(`/scm/procurement/supplier-invoices/${r.invoice_id}`)}>{v}</a>
      ),
    },
    {
      title: t('supplier'),
      dataIndex: 'supplier_name',
      key: 'supplier_name',
      width: 180,
      ellipsis: true,
    },
    {
      title: t('due'),
      dataIndex: 'due_date',
      key: 'due_date',
      width: 110,
      render: (v: string) => <span style={{ whiteSpace: 'nowrap' }}>{v || '—'}</span>,
    },
    ...(isOverdueMode
      ? ([
          {
            title: t('controlTower.aging'),
            dataIndex: 'overdue_days',
            key: 'overdue_days',
            width: 120,
            defaultSortOrder: 'descend' as const,
            sorter: (a: SupplierOutstandingDetailRow, b: SupplierOutstandingDetailRow) =>
              a.overdue_days - b.overdue_days,
            render: (d: number) => {
              const bucket = agingBucketFromDays(d);
              return (
                <Tag color={agingColor[bucket]} style={{ marginInlineEnd: 0 }}>
                  {t('controlTower.agingDaysBucket', { days: d, bucket })}
                </Tag>
              );
            },
          },
        ] as ColumnsType<SupplierOutstandingDetailRow>)
      : []),
    {
      title: t('balance'),
      dataIndex: 'balance_due',
      key: 'balance_due',
      align: 'right',
      width: 120,
      render: (v: number) => (
        <span style={{ whiteSpace: 'nowrap' }}>{formatCurrency(v ?? 0)}</span>
      ),
    },
    {
      title: '',
      key: 'action',
      width: 80,
      fixed: 'right',
      render: (_: unknown, r) => (
        <Button
          type="link"
          size="small"
          onClick={() => navigate(`/scm/procurement/supplier-invoices/${r.invoice_id}`)}
        >
          {t('open')}
        </Button>
      ),
    },
  ];

  const tableBody =
    isError ? (
      <Alert
        type="error"
        showIcon
        message={(error as Error)?.message ?? t('controlTower.failedLoadQueue')}
      />
    ) : isFetching && !data ? (
      <Spin />
    ) : rows.length === 0 ? (
      <EmptyQueueState message={t('controlTower.emptyQueueFilters')} />
    ) : (
      <Table
        rowKey="invoice_id"
        size="small"
        tableLayout="fixed"
        loading={isFetching}
        dataSource={rows as any}
        columns={(isAr ? arColumns : apColumns) as any}
        scroll={{ x: 900 }}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
          showTotal: (tot, range) =>
            t('controlTower.paginationRange', { from: range[0], to: range[1], total: tot }),
          onChange: (nextPage, nextSize) => {
            setPage(nextPage);
            if (nextSize && nextSize !== pageSize) {
              setPageSize(nextSize);
              setPage(1);
            }
          },
        }}
      />
    );

  return (
    <ControlTowerSectionCard
      zone="overdue"
      sectionId="collections-payables"
      title={
        <Space>
          <WarningOutlined />
          <span>
            {t('controlTower.collectionsPayablesTitle', { ar: arOverdue, ap: apOverdue })}
          </span>
        </Space>
      }
      extra={
        <Space>
          <Button
            type="link"
            size="small"
            onClick={() => navigate('/fin/reports/customer-outstanding')}
          >
            {t('controlTower.arRegister')}
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => navigate('/fin/reports/supplier-outstanding')}
          >
            {t('controlTower.apRegister')}
          </Button>
        </Space>
      }
    >
      <ControlTowerMetricStrip zone="metricsPayables" title={t('controlTower.cashRisk')}>
        <Col xs={24} sm={12} md={6}>
          <ControlTowerMetricTile
            title={t('controlTower.overdueAr')}
            count={arOverdue}
            amount={summary.overdue_customer_invoice_value}
            formatCurrency={formatCurrency}
            icon={<WarningOutlined />}
            urgency={arOverdue > 0 ? 'critical' : 'neutral'}
            footerLink={{
              label: t('controlTower.followUp'),
              onClick: () => {
                setMode('overdue_ar');
                setPage(1);
              },
            }}
          />
        </Col>
        <Col xs={24} sm={12} md={6}>
          <ControlTowerMetricTile
            title={t('controlTower.overdueAp')}
            count={apOverdue}
            amount={summary.overdue_supplier_invoice_value}
            formatCurrency={formatCurrency}
            icon={<WarningOutlined />}
            urgency={apOverdue > 0 ? 'critical' : 'neutral'}
            footerLink={{
              label: t('controlTower.followUp'),
              onClick: () => {
                setMode('overdue_ap');
                setPage(1);
              },
            }}
          />
        </Col>
        <Col xs={24} sm={12} md={6}>
          <ControlTowerMetricTile
            title={t('controlTower.arDueSoon')}
            count={arDueSoon}
            amount={summary.due_soon_customer_invoice_value}
            formatCurrency={formatCurrency}
            icon={<ClockCircleOutlined />}
            urgency={arDueSoon > 0 ? 'attention' : 'neutral'}
            footerLink={{
              label: tc('view'),
              onClick: () => {
                setMode('due_soon_ar');
                setAging(undefined);
                setPage(1);
              },
            }}
          />
        </Col>
        <Col xs={24} sm={12} md={6}>
          <ControlTowerMetricTile
            title={t('controlTower.apDueSoon')}
            count={apDueSoon}
            amount={summary.due_soon_supplier_invoice_value}
            formatCurrency={formatCurrency}
            icon={<ClockCircleOutlined />}
            urgency={apDueSoon > 0 ? 'attention' : 'neutral'}
            footerLink={{
              label: tc('view'),
              onClick: () => {
                setMode('due_soon_ap');
                setAging(undefined);
                setPage(1);
              },
            }}
          />
        </Col>
      </ControlTowerMetricStrip>

      <Alert
        type="info"
        showIcon
        style={{ marginTop: 12, marginBottom: 12 }}
        message={t('controlTower.fullQueueHint')}
      />

      <Space wrap style={{ marginBottom: 12 }}>
        <Input.Search
          allowClear
          placeholder={isAr ? t('controlTower.searchInvoiceCustomer') : t('controlTower.searchBillSupplier')}
          style={{ width: 280 }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onSearch={(v) => {
            setSearchApplied(v.trim());
            setPage(1);
          }}
        />
        {isOverdueMode ? (
          <Select
            allowClear
            placeholder={t('controlTower.aging')}
            style={{ width: 140 }}
            value={aging}
            onChange={(v) => {
              setAging(v);
              setPage(1);
            }}
            options={[
              {
                value: '0-30',
                label: t('controlTower.agingBucket', {
                  bucket: '0–30',
                  count: agingCounts?.['0-30']?.count ?? 0,
                }),
              },
              {
                value: '31-60',
                label: t('controlTower.agingBucket', {
                  bucket: '31–60',
                  count: agingCounts?.['31-60']?.count ?? 0,
                }),
              },
              {
                value: '61-90',
                label: t('controlTower.agingBucket', {
                  bucket: '61–90',
                  count: agingCounts?.['61-90']?.count ?? 0,
                }),
              },
              {
                value: '90+',
                label: t('controlTower.agingBucket', {
                  bucket: '90+',
                  count: agingCounts?.['90+']?.count ?? 0,
                }),
              },
            ]}
          />
        ) : null}
        <Button size="small" onClick={() => refetch()} loading={isFetching}>
          {t('controlTower.refreshQueue')}
        </Button>
        {totalValue > 0 ? (
          <Text type="secondary">
            {total === 1
              ? t('controlTower.queueValue', { amount: formatCurrency(totalValue), count: total })
              : t('controlTower.queueValuePlural', {
                  amount: formatCurrency(totalValue),
                  count: total,
                })}
          </Text>
        ) : null}
      </Space>

      <Tabs
        size="small"
        activeKey={mode}
        onChange={(k) => {
          setMode(k as CollectionsPayablesQueueMode);
          setPage(1);
          if (!String(k).startsWith('overdue_')) setAging(undefined);
        }}
        items={[
          {
            key: 'overdue_ar',
            label: t('controlTower.tabOverdueAr', { count: arOverdue }),
            children: mode === 'overdue_ar' ? tableBody : null,
          },
          {
            key: 'overdue_ap',
            label: t('controlTower.tabOverdueAp', { count: apOverdue }),
            children: mode === 'overdue_ap' ? tableBody : null,
          },
          {
            key: 'due_soon_ar',
            label: t('controlTower.tabDueSoonAr', { count: arDueSoon }),
            children: mode === 'due_soon_ar' ? tableBody : null,
          },
          {
            key: 'due_soon_ap',
            label: t('controlTower.tabDueSoonAp', { count: apDueSoon }),
            children: mode === 'due_soon_ap' ? tableBody : null,
          },
        ]}
      />
    </ControlTowerSectionCard>
  );
};

export default FinanceOverdueFollowUpZone;
